import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ShareApp } from "@/components/ShareApp";
import { PublicStats } from "@/components/PublicStats";
import {
  Sparkles,
  FileText,
  Globe,
  Lock,
  Layers,
  Crown,
  Download,
  ArrowRight,
  Check,
  BookOpen,
  Camera,
  Share2,
  Printer,
  Moon,
  Smartphone,
  Zap,
  BookMarked,
  Link as LinkIcon,
  ScrollText,
  History,
} from "lucide-react";

const Section = ({ marker, title, children, className = "" }) => (
  <section className={`border-b border-border ${className}`}>
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
      <div className="sux-marker mb-3">{marker}</div>
      <h2
        className="text-3xl md:text-4xl font-black tracking-tight mb-8 leading-tight"
        style={{ fontFamily: "Outfit" }}
      >
        {title}
      </h2>
      {children}
    </div>
  </section>
);

const Feature = ({ icon: Icon, title, body }) => (
  <div className="flex items-start gap-4 py-4 border-b border-border last:border-b-0">
    <div className="w-9 h-9 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="font-semibold text-base mb-1">{title}</div>
      <div className="text-sm text-muted-foreground leading-relaxed">{body}</div>
    </div>
  </div>
);

// Map of category-icon names → lucide components (used by the "Every feature"
// section below).
const ICONS = {
  Layers,
  FileText,
  Sparkles,
  Zap,
  Share2,
  Smartphone,
  BookOpen,
  Camera,
  Globe,
  Lock,
  BookMarked,
  History,
  ScrollText,
  Printer,
  Moon,
  Link: LinkIcon,
};

export default function About() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isFr = i18n.resolvedLanguage === "fr";

  const txt = isFr
    ? {
        hero_marker: "POUR ÉTUDIANTS, CHERCHEURS & ENSEIGNANTS",
        hero_title_a: "Un PDF de 200 pages.",
        hero_title_b: "Résumé en 5 secondes.",
        hero_subtitle:
          "SUX1 est l'outil pensé pour ceux qui lisent beaucoup : cours, thèses, articles scientifiques, chapitres entiers, ouvrages Gutenberg. Là où ChatGPT vous demande de couper le texte, d'ouvrir une conversation, de re-prompter — SUX1 avale un livre entier d'un clic. Dès 2 € les 20 résumés. Pas d'abonnement.",
        cta_try: "Voir les exemples gratuits",
        cta_pdf: "Télécharger la présentation PDF",
        video_left_label: "Article · 200 pages",
        video_right_label: "Résumé · 5 lignes",
        video_caption: "SUX1 · Distillé en 3 secondes",
        problem_marker: "01 · LE PROBLÈME",
        problem_title: "Vous lisez plus que jamais. Vous comprenez moins.",
        problem_body_1:
          "Notre quotidien numérique nous noie sous le texte : articles, rapports, contrats, mails, transcriptions de réunions, posts LinkedIn de 12 paragraphes. Le temps de lecture, lui, n'a pas suivi.",
        problem_body_2:
          "Les IA grand public (ChatGPT, Claude, Gemini) sont devenues des outils géniaux, mais lourds pour une simple tâche : « résume-moi ça ». Il faut ouvrir une session, taper un prompt, copier-coller, attendre. SUX1 fait disparaître cette friction.",
        solution_marker: "02 · LA SOLUTION",
        solution_title: "Un seul écran. Un seul bouton.",
        solution_body:
          "SUX1 est conçu pour un usage unique : résumer. Vous collez votre texte (ou téléversez un PDF, un DOCX, ou collez l'URL d'un article), vous choisissez vos options, vous cliquez Résumer. C'est tout.",
        vs_marker: "02b · SUX1 vs CHATGPT GRATUIT",
        vs_title: "« Mais ChatGPT le fait gratuitement ? »",
        vs_intro:
          "Oui, ChatGPT peut résumer un texte court. Non, il ne peut pas faire ce que SUX1 fait. Comparons honnêtement :",
        vs_headers: ["Critère", "ChatGPT Gratuit", "SUX1 · dès 2 €"],
        vs_rows: [
          ["Longueur maximum d'un texte", "≈ 8 000 mots (32k tokens) — au-delà, il coupe silencieusement", "Livre entier · jusqu'à 300 000 caractères, découpé en map-reduce automatique"],
          ["PDF de thèse (200 pages)", "Non — il faut copier-coller manuellement, chapitre par chapitre", "Glissez le fichier · résumé complet en streaming"],
          ["EPUB / Project Gutenberg", "Non supporté", "Import natif · résumé global ou par chapitre"],
          ["Article web (URL)", "Non — vous devez copier le texte à la main", "Collez l'URL · Trafilatura extrait le contenu propre"],
          ["Photo de couverture de livre", "Non", "Prenez la photo · fiche de lecture générée"],
          ["Résumé chapitre par chapitre", "Non — un seul bloc de sortie", "Accordéon · jusqu'à 40 chapitres détectés"],
          ["Historique persistant recherchable", "3 dernières conversations en pagaille", "Historique dédié · recherche, tri, tags"],
          ["Partage public (lien Open Graph)", "Non", "Chaque résumé devient une page publique partageable"],
          ["Lecture vocale hors-ligne", "Non", "Bouton Écouter · voix FR/EN natives, gratuit"],
          ["3 IA au choix pour le même résumé", "Vous êtes bloqué sur un seul modèle", "Claude Sonnet 4.5 · GPT-5.2 · Gemini 3 Flash en un clic"],
          ["Format « Explique & Résous »", "Prompt à réécrire à chaque fois", "Bouton dédié · idéal pour exercices, TDs, sujets d'examen"],
          ["Prix", "0 € · mais limites cachées + pubs pour Plus (20 $/mois)", "Dès 2 € les 20 résumés · crédits sans expiration · sans abonnement"],
          ["Vie privée", "Vos textes peuvent servir à entraîner OpenAI", "Vos textes ne sont jamais utilisés pour entraîner l'IA"],
        ],
        vs_conclusion:
          "En résumé : ChatGPT est un couteau suisse. SUX1 est un rasoir suisse. Si résumer est votre activité quotidienne — étudiant qui prépare un partiel, chercheur qui digère 30 papers par semaine, enseignant qui prépare un cours — l'un vous fait perdre du temps, l'autre vous en fait gagner.",
        features_marker: "03 · CE QU'IL Y A SOUS LE CAPOT",
        features_title: "Le meilleur de l'IA, sans la complexité.",
        f1: "3 modèles d'IA au choix",
        f1_body:
          "Claude Sonnet 4.5, GPT-5.2, Gemini 3 Flash — interchangeables d'un clic. Chaque modèle a sa personnalité : l'un est plus littéraire, l'autre plus analytique, le troisième plus rapide.",
        f2: "5 sources d'entrée",
        f2_body:
          "Texte collé, PDF, DOCX, TXT/MD, URL d'article (extraction propre du contenu — ignore pubs et menus grâce à Trafilatura), Google Docs publics, et scan photo de couverture de livre.",
        f3: "3 longueurs · 12 formats · 7 langues",
        f3_body:
          "Court, moyen, long. Paragraphe, points clés, paragraphe+points, Enseignant, Fiche de révision, Fiche DYS, Métaphores, Poème et récitation, Fiche encyclopédique, Philo+citations, Dissertation, Explication & Résolution. Auto-détection ou langue forcée (FR, EN, ES, DE, IT, PT).",
        f4: "Partage public + Open Graph",
        f4_body:
          "Chaque résumé peut devenir un lien public avec aperçu Open Graph dynamique — partageable sur LinkedIn, X, WhatsApp avec une belle carte.",
        f5: "Vie privée respectée",
        f5_body:
          "Vos textes ne sont jamais utilisés pour entraîner d'IA tierce. Vous pouvez supprimer vos résumés ou votre compte à tout moment.",
        all_features_marker: "04 · TOUTES LES FONCTIONNALITÉS",
        all_features_title: "Un couteau suisse pour tout texte.",
        all_features_intro:
          "SUX1 a démarré avec un simple champ « collez votre texte ». Il gère aujourd'hui un livre entier en streaming, un scan de couverture, une URL Wikipedia, un contrat PDF ou une transcription YouTube — avec la même simplicité d'un clic.",
        cats: [
          {
            title: "3 modes de résumé",
            icon: "Layers",
            items: [
              ["Résumé global", "Un résumé synthétique du document entier, dans le format et la longueur choisis."],
              ["Résumé par chapitre", "Détection automatique des chapitres (jusqu'à 40) et résumé de chacun, en accordéon — parfait pour un livre ou un long rapport."],
              ["Scan d'un livre", "Prenez en photo la couverture d'un livre : SUX1 identifie titre + auteur via Claude Vision, puis produit une fiche de lecture complète, sans stocker l'image."],
            ],
          },
          {
            title: "5 sources d'entrée",
            icon: "FileText",
            items: [
              ["Texte collé", "Jusqu'à ~300 000 caractères en global, 300 000 en mode chapitre."],
              ["Fichier PDF / DOCX / TXT / MD", "Extraction propre, avec correction automatique des ligatures cassées (« fl », « ﬀ »…)."],
              ["URL d'article ou de blog", "Extraction Trafilatura : pas de pubs, pas de menus, juste le contenu. Supporte les redirections Google Docs publics."],
              ["Photo de couverture", "Ouvre directement l'appareil photo mobile via l'API `capture=\"environment\"`."],
              ["Project Gutenberg & livres longs", "Découpage automatique en 16 sections avec pipeline map-reduce en streaming."],
            ],
          },
          {
            title: "Options fines par résumé",
            icon: "Sparkles",
            items: [
              ["3 longueurs", "Court (2-4 phrases), Moyen (paragraphe), Long (jusqu'à 1 500 mots)."],
              ["12 formats", "Paragraphe fluide, Points-clés, Paragraphe+Points, Enseignant (préparation de cours + pièges), Fiche de révision, Fiche de révision DYS, Fiche encyclopédique, Philo+citations, Dissertation, Explication & Résolution (résout tout exercice pas à pas)."],
              ["Métaphores (résumé imagé)", "« En bref », puis chaque idée clé expliquée par une image du quotidien (« C'est comme… ») et sa « Limite », pour ne pas induire en erreur. Adapté au niveau de classe. Une simple notion suffit (ex : « la photosynthèse »)."],
              ["Poème et récitation", "La leçon devient un poème rimé de 3 à 6 strophes, suivi de « Ce qu'il faut retenir » et de conseils « Pour bien le réciter ». Lecture à voix haute gratuite, lente, strophe par strophe (Répéter, Suivante) pour l'apprendre par cœur."],
              ["7 langues", "Auto-détection, ou force en FR, EN, ES, DE, IT, PT."],
              ["3 IA au choix", "Claude Sonnet 4.5 (littéraire), GPT-5.2 (analytique), Gemini 3 Flash (rapide)."],
            ],
          },
          {
            title: "Expérience utilisateur",
            icon: "Zap",
            items: [
              ["Streaming en direct", "Le résumé s'écrit mot par mot sous vos yeux — effet machine à écrire."],
              ["Progression visible", "En mode chapitre : « 12 / 40 chapitres résumés… » avec barre de progression."],
              ["Résumé partiel préservé", "Si la connexion est coupée pendant un long résumé, ce qui a été généré s'affiche quand même."],
              ["Lecture vocale native", "Bouton « Écouter » qui lit le résumé à voix haute via la synthèse vocale du navigateur — gratuit, hors-ligne, voix FR/EN natives sur iOS et Android."],
              ["Bannières d'erreur claires", "Fini les échecs silencieux — chaque erreur est expliquée et suggère une action."],
              ["Mode sombre", "Thème sombre soigné pour lire le soir."],
              ["Barre de résumé sticky mobile", "Le bouton Résumer reste toujours accessible en bas d'écran sur mobile."],
              ["Correction orthographique automatique", "PDF avec ligatures cassées ? Le prompt système corrige avant de résumer."],
            ],
          },
          {
            title: "Partage et export",
            icon: "Share2",
            items: [
              ["Lien public + Open Graph", "Chaque résumé devient une page publique avec belle carte de partage LinkedIn / X / WhatsApp."],
              ["Web Share API", "Ouvre la feuille de partage native du système (iOS, Android)."],
              ["Impression PDF", "Bouton d'impression avec mise en page optimisée."],
              ["Historique persistant", "Retrouvez tous vos résumés dans l'onglet Historique — recherche, tri, suppression."],
              ["Mot de passe modifiable", "Depuis les paramètres, sans email de reset."],
            ],
          },
          {
            title: "PWA & mobile",
            icon: "Smartphone",
            items: [
              ["Installation en un clic", "SUX1 est une Progressive Web App — ajoutez-la à l'écran d'accueil (iOS/Android) pour un lancement natif."],
              ["Google Play Store", "Version Android disponible en test fermé (bientôt en production)."],
              ["Splash screen dédié", "Écran de lancement CSS natif, jamais de page blanche."],
              ["Feedback tactile", "Micro-animation sur chaque tap, comme une vraie app native."],
            ],
          },
        ],
        pricing_marker: "05 · MODÈLE ÉCONOMIQUE",
        pricing_title: "Dès 2 €. Sans abonnement.",
        pricing_free: "GRATUIT",
        pricing_pro: "PACKS DE CRÉDITS",
        pricing_free_price: "0 €",
        pricing_pro_price: "dès 2 €",
        pricing_free_li: [
          "6 exemples gratuits",
          "Avec Gemini 3 Flash",
          "Textes jusqu'à ~20 pages",
          "Sans carte demandée",
        ],
        pricing_pro_li: [
          "20 crédits 2 € · 60 crédits 5 € · 130 crédits 10 €",
          "Tous les modèles inclus",
          "Toutes les sources",
          "Partage public + Open Graph",
          "Crédits sans expiration · sans abonnement",
        ],
        pricing_cta_free: "Voir les exemples gratuits →",
        pricing_cta_pro: "Voir les packs · dès 2 € →",
        why_marker: "06 · POURQUOI SUX1",
        why_title: "Cinq principes.",
        why_li: [
          ["Minimaliste", "Un seul écran. Un seul bouton."],
          ["Honnête", "Vous payez ce que vous utilisez. Pas d'abonnement piégeux."],
          ["Précis", "Résumés fidèles, jamais inventés. Citations préservées."],
          ["Privé", "Vos textes ne quittent pas le serveur."],
          ["Beau", "Design Swiss & typographie soignée."],
        ],
        changelog_marker: "07 · JOURNAL DES AMÉLIORATIONS",
        changelog_title: "Ce qui a changé récemment.",
        changelog_intro:
          "SUX1 évolue en continu. Voici les améliorations majeures apportées dans les dernières semaines.",
        changelog_items: [
          {
            v: "v1.6",
            date: "9 août 2026",
            title: "Lecture vocale native (Web Speech API)",
            body:
              "Bouton « Écouter » à côté de chaque résumé — global, par chapitre, ou depuis l'archive. Utilise la synthèse vocale native du navigateur : gratuit, hors-ligne, voix françaises et anglaises de qualité sur iOS, macOS, Chrome et Android. Play / Pause / Stop, détection automatique de langue, coupure automatique à la navigation.",
          },
          {
            v: "v1.5",
            date: "Août 2026",
            title: "Résumé global de livres entiers (streaming map-reduce)",
            body:
              "Pipeline en deux phases : découpage automatique en 16 sections résumées en parallèle par Gemini Flash, puis synthèse finale par le modèle de votre choix. Le texte s'écrit token par token en direct dans l'UI. Si la connexion coupe, le résumé partiel est préservé.",
          },
          {
            v: "v1.4",
            date: "Août 2026",
            title: "Mode chapitre en streaming (batches de 5)",
            body:
              "Détection automatique jusqu'à 40 chapitres. Chaque chapitre arrive au fur et à mesure dans un accordéon. Barre de progression visible en temps réel. Timeout par chapitre pour éviter tout blocage.",
          },
          {
            v: "v1.3",
            date: "Juillet 2026",
            title: "« Scan a Book » — fiche de lecture depuis une photo",
            body:
              "Prenez en photo la couverture d'un livre : Claude Vision identifie le titre et l'auteur, puis SUX1 produit une fiche de lecture complète — sans stocker l'image.",
          },
          {
            v: "v1.2",
            date: "Juillet 2026",
            title: "Extraction URL améliorée",
            body:
              "Remplacement de BeautifulSoup par Trafilatura : plus de pubs, plus de menus, juste le contenu. Support natif des redirections Google Docs publics.",
          },
          {
            v: "v1.1",
            date: "Juin 2026",
            title: "PWA + Google Play (test fermé)",
            body:
              "App installable en un clic sur iOS et Android. Splash screen CSS natif. Feedback tactile sur chaque tap. AAB conforme API 36 déposé sur Google Play Console.",
          },
          {
            v: "v1.0",
            date: "Juin 2026",
            title: "Partage, impression, mode sombre, format Philo+citations",
            body:
              "Web Share API pour partage natif, impression PDF optimisée, thème sombre soigné, et nouveau format « Philo » : idée centrale + perspective philosophique + citations d'auteurs.",
          },
        ],
        share_marker: "08 · AIDEZ-NOUS À GRANDIR",
        share_title: "Partagez SUX1.",
        share_body:
          "Si vous trouvez l'outil utile, le meilleur cadeau est d'en parler autour de vous.",
        footer_made: "Conçu avec soin · Édition 2026",
      }
    : {
        hero_marker: "BUILT FOR STUDENTS, RESEARCHERS & TEACHERS",
        hero_title_a: "A 200-page PDF.",
        hero_title_b: "Summarised in 5 seconds.",
        hero_subtitle:
          "SUX1 is the tool for people who read a lot: courses, theses, scientific papers, whole chapters, entire Gutenberg books. Where ChatGPT asks you to split the text, open a chat, re-prompt — SUX1 swallows a full book in one click. From €2 for 20 summaries. No subscription.",
        cta_try: "Try it free",
        cta_pdf: "Download the PDF presentation",
        video_left_label: "Article · 200 pages",
        video_right_label: "Summary · 5 lines",
        video_caption: "SUX1 · Distilled in 3 seconds",
        problem_marker: "01 · THE PROBLEM",
        problem_title: "You read more than ever. You understand less.",
        problem_body_1:
          "Our digital lives drown us in text: articles, reports, contracts, emails, meeting transcripts, 12-paragraph LinkedIn posts. Reading time hasn't kept up.",
        problem_body_2:
          "General-purpose AIs (ChatGPT, Claude, Gemini) are amazing — but overkill for one simple task: 'summarize this'. SUX1 removes that friction.",
        solution_marker: "02 · THE SOLUTION",
        solution_title: "One screen. One button.",
        solution_body:
          "SUX1 is built for one use case: summarizing. Paste text (or upload a PDF, DOCX, or paste an article URL), pick your options, click Summarize. That's it.",
        vs_marker: "02b · SUX1 vs FREE CHATGPT",
        vs_title: "\"But ChatGPT does it for free?\"",
        vs_intro:
          "Yes, ChatGPT can summarise a short text. No, it cannot do what SUX1 does. Honest comparison:",
        vs_headers: ["Feature", "Free ChatGPT", "SUX1 · from €2"],
        vs_rows: [
          ["Max input length", "≈ 8 000 words (32k tokens) — silently truncates beyond", "Whole book · up to 300 000 chars via auto map-reduce"],
          ["200-page thesis PDF", "No — you must copy-paste chapter by chapter", "Drop the file · full streaming summary"],
          ["EPUB / Project Gutenberg", "Not supported", "Native import · global or chapter mode"],
          ["Web article (URL)", "No — you paste the text manually", "Paste the URL · Trafilatura extracts clean content"],
          ["Book cover photo", "No", "Snap the cover · full reading sheet"],
          ["Chapter-by-chapter summary", "No — single output blob", "Accordion · up to 40 detected chapters"],
          ["Searchable persistent history", "Last 3 chats in a mess", "Dedicated history · search, sort, tags"],
          ["Public sharing (Open Graph link)", "No", "Every summary becomes a shareable public page"],
          ["Offline voice reading", "No", "Listen button · native FR/EN voices, free"],
          ["3 AIs to choose per summary", "You're locked on one model", "Claude Sonnet 4.5 · GPT-5.2 · Gemini 3 Flash in one click"],
          ["\"Explain & Solve\" format", "Re-write the prompt every time", "Dedicated button · perfect for exercises & exams"],
          ["Price", "€0 · but hidden caps + ads for Plus ($20/month)", "From €2 for 20 summaries · credits never expire · no subscription"],
          ["Privacy", "Your texts may train OpenAI models", "Your texts are never used to train any AI"],
        ],
        vs_conclusion:
          "In short: ChatGPT is a swiss-army knife. SUX1 is a swiss razor. If summarising is your daily job — student cramming for an exam, researcher digesting 30 papers a week, teacher prepping a class — one wastes your time, the other saves it.",
        features_marker: "03 · UNDER THE HOOD",
        features_title: "The best of AI, without the complexity.",
        f1: "3 AI models, your choice",
        f1_body:
          "Claude Sonnet 4.5, GPT-5.2, Gemini 3 Flash — switch in one click. Each model has its character: literary, analytical, fastest.",
        f2: "5 input sources",
        f2_body:
          "Pasted text, PDF, DOCX, TXT/MD, article URL (auto-extracted with Trafilatura, ignores ads and nav), public Google Docs, and book-cover photo scan.",
        f3: "3 lengths · 12 formats · 7 languages",
        f3_body:
          "Short, medium, long. Paragraph, bullets, or Philo+citations. Auto-detect or force a language (FR, EN, ES, DE, IT, PT).",
        f4: "Public share + Open Graph",
        f4_body:
          "Every summary can become a public link with a dynamic Open Graph preview — shareable on LinkedIn, X, WhatsApp.",
        f5: "Privacy first",
        f5_body:
          "Your texts are never used to train third-party AIs. Delete summaries or your account anytime.",
        all_features_marker: "04 · EVERY FEATURE",
        all_features_title: "A swiss-army knife for any text.",
        all_features_intro:
          "SUX1 started as a plain \"paste your text\" field. Today it handles an entire book in streaming, a cover-photo scan, a Wikipedia URL, a PDF contract or a YouTube transcript — with the same one-click simplicity.",
        cats: [
          {
            title: "3 summary modes",
            icon: "Layers",
            items: [
              ["Global summary", "A synthetic summary of the whole document, in your chosen length and format."],
              ["Chapter by chapter", "Auto-detects up to 40 chapters and summarises each — perfect for a book or a long report."],
              ["Scan a book", "Snap a book's cover: SUX1 identifies title + author via Claude Vision, then produces a full reading sheet, without storing the image."],
            ],
          },
          {
            title: "5 input sources",
            icon: "FileText",
            items: [
              ["Pasted text", "Up to ~300 000 characters in either mode."],
              ["PDF / DOCX / TXT / MD file", "Clean extraction, with automatic repair of broken ligatures (\"fl\", \"ﬀ\"…)."],
              ["Article or blog URL", "Trafilatura extraction: no ads, no menus, just content. Supports public Google Docs redirects."],
              ["Book cover photo", "Opens the mobile camera directly via `capture=\"environment\"`."],
              ["Project Gutenberg & long books", "Auto-chunked into 16 sections through a streaming map-reduce pipeline."],
            ],
          },
          {
            title: "Fine-grained options",
            icon: "Sparkles",
            items: [
              ["3 lengths", "Short (2-4 sentences), Medium (paragraph), Long (up to 1 500 words)."],
              ["12 formats", "Flowing paragraph, Key points, Paragraph+Points, Teacher (lesson prep + pitfalls), Revision sheet, DYS revision sheet, Encyclopedic sheet, Philo+citations, Essay, Explain & Solve (walks through any exercise step by step)."],
              ["Metaphors (image-based summary)", "“In a nutshell”, then each key idea explained with an everyday image (“It's like…”) and its “Limit” so it never misleads. Adapted to the class level. A single notion is enough (e.g. “photosynthesis”)."],
              ["Poem & recitation", "The lesson becomes a 3-6 stanza rhyming poem, followed by “Key takeaways” and “How to recite it” tips. Free, slow read-aloud, stanza by stanza (Repeat, Next) to learn it by heart."],
              ["7 languages", "Auto-detect, or force FR, EN, ES, DE, IT, PT."],
              ["3 AI models", "Claude Sonnet 4.5 (literary), GPT-5.2 (analytical), Gemini 3 Flash (fastest)."],
            ],
          },
          {
            title: "User experience",
            icon: "Zap",
            items: [
              ["Live streaming", "The summary types itself before your eyes."],
              ["Visible progress", "In chapter mode: \"12 / 40 chapters done…\" with a progress bar."],
              ["Partial rescue", "If the connection drops during a long summary, what was generated is shown anyway."],
              ["Native voice reading", "\"Listen\" button that reads the summary aloud via the browser's speech synthesis — free, offline, native FR/EN voices on iOS and Android."],
              ["Clear error banners", "No more silent failures — every error is explained and suggests an action."],
              ["Dark mode", "Polished dark theme for evening reading."],
              ["Sticky mobile CTA", "The Summarize button is always reachable at the bottom of the screen."],
              ["Auto spell repair", "PDF with broken ligatures? The system prompt fixes them before summarizing."],
            ],
          },
          {
            title: "Sharing and export",
            icon: "Share2",
            items: [
              ["Public link + Open Graph", "Each summary becomes a public page with a beautiful share card."],
              ["Web Share API", "Opens the native OS share sheet (iOS, Android)."],
              ["Print to PDF", "Print button with an optimised layout."],
              ["Persistent history", "Find every summary in the History tab — search, sort, delete."],
              ["Password change", "From Settings, no reset email needed."],
            ],
          },
          {
            title: "PWA & mobile",
            icon: "Smartphone",
            items: [
              ["One-click install", "SUX1 is a Progressive Web App — add it to your home screen (iOS/Android) for a native launch."],
              ["Google Play Store", "Android build in closed testing (soon in production)."],
              ["Dedicated splash", "Native CSS splash screen, no white flash."],
              ["Tap feedback", "Micro-animation on every tap, like a real native app."],
            ],
          },
        ],
        pricing_marker: "05 · PRICING",
        pricing_title: "From €2. No subscription.",
        pricing_free: "FREE",
        pricing_pro: "CREDIT PACKS",
        pricing_free_price: "€0",
        pricing_pro_price: "from €2",
        pricing_free_li: [
          "3 trial summaries",
          "With Gemini 3 Flash",
          "Texts up to ~20 pages",
          "No card required",
        ],
        pricing_pro_li: [
          "20 credits €2 · 60 credits €5 · 130 credits €10",
          "All AI models included",
          "All sources",
          "Public share + Open Graph",
          "Credits never expire · no subscription",
        ],
        pricing_cta_free: "Start free →",
        pricing_cta_pro: "See packs · from €2 →",
        why_marker: "06 · WHY SUX1",
        why_title: "Five principles.",
        why_li: [
          ["Minimalist", "One screen. One button."],
          ["Honest", "Pay for what you use. No subscription tricks."],
          ["Accurate", "Faithful summaries. Nothing invented."],
          ["Private", "Your texts stay on the server."],
          ["Beautiful", "Swiss design & typography."],
        ],
        changelog_marker: "07 · CHANGELOG",
        changelog_title: "What's changed recently.",
        changelog_intro:
          "SUX1 keeps evolving. Here are the major improvements shipped in the last few weeks.",
        changelog_items: [
          {
            v: "v1.6",
            date: "Aug 9, 2026",
            title: "Native voice reading (Web Speech API)",
            body:
              "\"Listen\" button next to every summary — global, chapter, or archive detail. Uses the browser's native speech synthesis: free, offline, high-quality French and English voices on iOS, macOS, Chrome and Android. Play / Pause / Stop, auto language detection, auto-cancel on navigation.",
          },
          {
            v: "v1.5",
            date: "August 2026",
            title: "Whole-book global summary (streaming map-reduce)",
            body:
              "Two-phase pipeline: 16 sections digested in parallel by Gemini Flash, then a final synthesis by your model of choice. Text types itself token by token in the UI. If the connection is cut, the partial summary is preserved.",
          },
          {
            v: "v1.4",
            date: "August 2026",
            title: "Chapter mode with streaming (batches of 5)",
            body:
              "Auto-detects up to 40 chapters. Each chapter streams progressively into an accordion. Live progress bar. Per-chapter timeout prevents any hang.",
          },
          {
            v: "v1.3",
            date: "July 2026",
            title: '"Scan a Book" — reading sheet from a photo',
            body:
              "Snap a book's cover: Claude Vision identifies title and author, then SUX1 produces a full reading sheet — without storing the image.",
          },
          {
            v: "v1.2",
            date: "July 2026",
            title: "Better URL extraction",
            body:
              "BeautifulSoup replaced with Trafilatura: no more ads, no more menus, just content. Native support for public Google Docs redirects.",
          },
          {
            v: "v1.1",
            date: "June 2026",
            title: "PWA + Google Play (closed testing)",
            body:
              "One-click installable on iOS and Android. Native CSS splash screen. Tap feedback on every interaction. API 36-compliant AAB uploaded to Google Play Console.",
          },
          {
            v: "v1.0",
            date: "June 2026",
            title: "Sharing, printing, dark mode, Philo+citations",
            body:
              "Web Share API for native sharing, optimised PDF printing, polished dark theme, and a new \"Philo\" format: central idea + philosophical perspective + author quotes.",
          },
        ],
        share_marker: "08 · HELP US GROW",
        share_title: "Share SUX1.",
        share_body:
          "If you find the tool useful, the best gift is telling others.",
        footer_made: "Crafted with care · Edition 2026",
      };

  return (
    <div className="min-h-screen bg-card">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="h-16 max-w-5xl mx-auto flex items-center px-6 md:px-10 justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-block w-7 h-7 bg-[#002FA7]" />
            <span
              className="font-black text-2xl tracking-tight"
              style={{ fontFamily: "Outfit" }}
            >
              SUX<span className="text-[#FF2A00]">1</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to={user ? "/" : "/login"}
              className="font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white transition-colors"
            >
              {user ? t("nav.summarize") : t("nav.signin")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border bg-gradient-to-b from-secondary/30 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 lg:py-28">
          <div className="sux-marker mb-4">{txt.hero_marker}</div>
          <h1
            className="text-5xl md:text-7xl font-black tracking-tight leading-[0.95] mb-6"
            style={{ fontFamily: "Outfit" }}
          >
            {txt.hero_title_a}
            <br />
            <span className="text-[#FF2A00]">{txt.hero_title_b}</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-10">
            {txt.hero_subtitle}
          </p>

          {/* Sora 2 promo clip — muted autoplay loop, ~8s
              + FR overlay so the message stays crystal clear even
              though the AI-generated pseudo-text is decorative. */}
          <div
            data-testid="about-hero-video"
            className="relative mb-10 border border-border overflow-hidden bg-white shadow-sm max-w-3xl"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="w-full h-auto block"
              aria-label="SUX1 — animation de distillation"
            >
              <source src="/videos/sux1-hero.webm" type="video/webm" />
              <source src="/videos/sux1-hero.mp4" type="video/mp4" />
            </video>

            {/* French overlay — split-screen labels */}
            <div className="absolute inset-0 pointer-events-none flex">
              <div className="w-1/2 flex items-start justify-start p-4 md:p-6">
                <div className="bg-[#002FA7] text-white px-3 py-1.5 font-mono uppercase tracking-[0.2em] text-[10px] md:text-[11px]">
                  {txt.video_left_label}
                </div>
              </div>
              <div className="w-1/2 flex items-start justify-end p-4 md:p-6">
                <div className="bg-[#FF2A00] text-white px-3 py-1.5 font-mono uppercase tracking-[0.2em] text-[10px] md:text-[11px]">
                  {txt.video_right_label}
                </div>
              </div>
            </div>

            {/* Bottom-left caption */}
            <div className="absolute bottom-3 left-4 md:bottom-5 md:left-6 pointer-events-none">
              <div className="bg-[#002FA7]/70 text-white px-3 py-1.5 font-mono uppercase tracking-[0.2em] text-[10px] md:text-[11px] backdrop-blur-sm">
                {txt.video_caption}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={user ? "/" : "/signup"}
              data-testid="about-cta-try"
              className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#00227A] transition-colors flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {txt.cta_try}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a
              href="/sux1-presentation.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-download-pdf"
              className="bg-card text-foreground border border-border font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" />
              {txt.cta_pdf}
            </a>
          </div>

          <PublicStats variant="hero" />
        </div>
      </section>

      <Section marker={txt.problem_marker} title={txt.problem_title}>
        <p className="text-base leading-relaxed mb-4">{txt.problem_body_1}</p>
        <p className="text-base leading-relaxed">{txt.problem_body_2}</p>
      </Section>

      <Section marker={txt.solution_marker} title={txt.solution_title}>
        <p className="text-base leading-relaxed">{txt.solution_body}</p>
      </Section>

      {/* SUX1 vs Free ChatGPT — anti-objection comparison table.
          Targeted at the objection "ChatGPT does it for free". */}
      <Section
        marker={txt.vs_marker}
        title={txt.vs_title}
        className="bg-secondary/30"
      >
        <p
          className="text-base leading-relaxed mb-8 text-muted-foreground"
          data-testid="about-vs-intro"
        >
          {txt.vs_intro}
        </p>

        <div
          className="border border-border bg-card overflow-x-auto"
          data-testid="about-vs-table"
        >
          <table className="w-full text-sm border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-[#002FA7] text-white">
                <th className="text-left px-4 py-3 font-mono uppercase tracking-[0.15em] text-[10px] w-[28%]">
                  {txt.vs_headers[0]}
                </th>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-[0.15em] text-[10px] w-[36%]">
                  {txt.vs_headers[1]}
                </th>
                <th className="text-left px-4 py-3 font-mono uppercase tracking-[0.15em] text-[10px] w-[36%] bg-[#002FA7]">
                  {txt.vs_headers[2]}
                </th>
              </tr>
            </thead>
            <tbody>
              {txt.vs_rows.map(([label, chatgpt, sux1], idx) => (
                <tr
                  key={label}
                  className={idx % 2 === 0 ? "bg-card" : "bg-secondary/40"}
                  data-testid={`about-vs-row-${idx}`}
                >
                  <td className="px-4 py-3 font-semibold align-top border-r border-border">
                    {label}
                  </td>
                  <td className="px-4 py-3 align-top border-r border-border text-muted-foreground">
                    {chatgpt}
                  </td>
                  <td className="px-4 py-3 align-top bg-[#002FA7]/5">
                    <div className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-[#002FA7] dark:text-[#7B9CFF] mt-1 flex-shrink-0" />
                      <span className="text-foreground">{sux1}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p
          className="text-base leading-relaxed mt-8 italic border-l-4 border-[#FF2A00] pl-4"
          data-testid="about-vs-conclusion"
        >
          {txt.vs_conclusion}
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={user ? "/" : "/signup"}
            data-testid="about-vs-cta-try"
            className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#00227A] transition-colors flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {txt.cta_try}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to="/etudiants"
            data-testid="about-vs-cta-etudiants"
            className="bg-card text-foreground border border-border font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#FF2A00] hover:text-white transition-colors flex items-center gap-2"
          >
            {isFr
              ? "Étudiant ou chercheur ? Voir la page dédiée →"
              : "Student or researcher? See the dedicated page →"}
          </Link>
        </div>
      </Section>

      <Section marker={txt.features_marker} title={txt.features_title}>
        <div className="border border-border bg-card">
          <Feature icon={Layers} title={txt.f1} body={txt.f1_body} />
          <Feature icon={FileText} title={txt.f2} body={txt.f2_body} />
          <Feature icon={Globe} title={txt.f3} body={txt.f3_body} />
          <Feature icon={Sparkles} title={txt.f4} body={txt.f4_body} />
          <Feature icon={Lock} title={txt.f5} body={txt.f5_body} />
        </div>
      </Section>

      {/* Comprehensive feature list — grouped by category. */}
      <Section marker={txt.all_features_marker} title={txt.all_features_title}>
        <p className="text-base leading-relaxed mb-10 text-muted-foreground">
          {txt.all_features_intro}
        </p>
        <div className="space-y-8">
          {txt.cats.map((cat) => {
            const CatIcon = ICONS[cat.icon] || Sparkles;
            return (
              <div
                key={cat.title}
                className="border border-border bg-card"
                data-testid={`about-cat-${cat.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-3 px-5 py-4 border-b border-border bg-secondary/40">
                  <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
                    <CatIcon className="w-4 h-4" />
                  </div>
                  <h3
                    className="text-lg font-black tracking-tight"
                    style={{ fontFamily: "Outfit" }}
                  >
                    {cat.title}
                  </h3>
                </div>
                <ul className="divide-y divide-border">
                  {cat.items.map(([h, body]) => (
                    <li
                      key={h}
                      className="px-5 py-4 flex items-start gap-3"
                    >
                      <Check className="w-4 h-4 text-[#002FA7] dark:text-[#7B9CFF] mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-semibold text-sm mb-1">{h}</div>
                        <div className="text-sm text-muted-foreground leading-relaxed">
                          {body}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Section>

      <Section marker={txt.pricing_marker} title={txt.pricing_title}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border">
          <div className="p-8 border-r border-border">
            <div className="sux-marker mb-2">{txt.pricing_free}</div>
            <div
              className="text-5xl font-black mb-6"
              style={{ fontFamily: "Outfit" }}
            >
              {txt.pricing_free_price}
            </div>
            <ul className="space-y-2 mb-6">
              {txt.pricing_free_li.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-2 text-sm"
                >
                  <Check className="w-3.5 h-3.5 text-[#002FA7] dark:text-[#7B9CFF] mt-1 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/signup"
              className="block w-full text-center font-mono uppercase tracking-[0.25em] text-[11px] px-4 py-3 border border-border hover:bg-[#FF2A00] hover:text-white transition-colors"
            >
              {txt.pricing_cta_free}
            </Link>
          </div>
          <div className="p-8 bg-[#002FA7] text-white">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-white" />
              <span className="font-mono uppercase tracking-[0.2em] text-[10px] text-white/70">
                {txt.pricing_pro}
              </span>
            </div>
            <div
              className="text-5xl font-black mb-6 text-white"
              style={{ fontFamily: "Outfit" }}
            >
              {txt.pricing_pro_price}
            </div>
            <ul className="space-y-2 mb-6">
              {txt.pricing_pro_li.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-2 text-sm text-white/90"
                >
                  <Check className="w-3.5 h-3.5 text-white mt-1 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Link
              to={user ? "/billing/upgrade" : "/signup"}
              className="block w-full text-center bg-white text-[#002FA7] font-mono uppercase tracking-[0.25em] text-[11px] px-4 py-3 hover:bg-white/90 transition-colors"
            >
              {txt.pricing_cta_pro}
            </Link>
          </div>
        </div>
      </Section>

      <Section marker={txt.why_marker} title={txt.why_title}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border">
          {txt.why_li.map(([h, body], i) => (
            <div
              key={h}
              className={`p-6 border-b border-border ${
                i % 2 === 0 ? "md:border-r" : ""
              } ${i >= txt.why_li.length - 2 ? "md:border-b-0" : ""}`}
            >
              <div className="font-semibold text-base mb-1">{h}</div>
              <div className="text-sm text-muted-foreground">{body}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Changelog — recent improvements timeline */}
      <Section marker={txt.changelog_marker} title={txt.changelog_title}>
        <p className="text-base leading-relaxed mb-10 text-muted-foreground">
          {txt.changelog_intro}
        </p>
        <ol
          className="relative border-l-2 border-[#002FA7]/20 pl-6 md:pl-8 space-y-8"
          data-testid="about-changelog"
        >
          {txt.changelog_items.map((it, idx) => (
            <li key={it.v} className="relative">
              <span
                className={`absolute -left-[33px] md:-left-[41px] top-1 w-4 h-4 flex items-center justify-center border-2 ${
                  idx === 0
                    ? "bg-[#FF2A00] border-[#FF2A00]"
                    : "bg-background border-[#002FA7]"
                }`}
                aria-hidden="true"
              />
              <div className="flex flex-wrap items-baseline gap-3 mb-2">
                <span
                  className={`font-mono uppercase tracking-[0.2em] text-[10px] px-2 py-1 ${
                    idx === 0
                      ? "bg-[#FF2A00] text-white"
                      : "bg-[#002FA7]/10 text-[#002FA7] dark:text-[#7B9CFF]"
                  }`}
                >
                  {it.v}
                </span>
                <span className="sux-marker">{it.date}</span>
              </div>
              <h3
                className="text-xl md:text-2xl font-black tracking-tight mb-2 leading-tight"
                style={{ fontFamily: "Outfit" }}
              >
                {it.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {it.body}
              </p>
            </li>
          ))}
        </ol>
      </Section>

      <Section marker={txt.share_marker} title={txt.share_title}>
        <p className="text-base leading-relaxed mb-8">{txt.share_body}</p>
        <ShareApp />
      </Section>

      <footer className="py-10 px-6 md:px-10 text-center">
        <div className="sux-marker mb-3">{txt.footer_made}</div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link
            to="/etudiants"
            data-testid="about-etudiants-link"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            {isFr ? "Étudiants & chercheurs" : "Students & researchers"}
          </Link>
          <Link
            to="/education"
            data-testid="about-education-link"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            {isFr ? "Établissements scolaires" : "Schools & universities"}
          </Link>
          <a
            href={`mailto:fiestamax@gmail.com?subject=${encodeURIComponent("[SUX1] Contact")}`}
            data-testid="about-contact-link"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            {isFr ? "Contact" : "Contact"}
          </a>
          <a
            href={isFr ? "/guide.html" : "/guide-en.html"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-guide-link"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            {isFr ? "Guide d'utilisation" : "User guide"}
          </a>
          <Link
            to="/privacy"
            data-testid="about-privacy-link"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            {isFr ? "Politique de confidentialité" : "Privacy policy"}
          </Link>
        </div>
      </footer>
    </div>
  );
}
